import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  suggestContactNextActionTime,
  suggestThirtyDayContactReminder,
} from "./contactNextActionTime";

import {
  type ContactNextAction,
  type ContactNextActionInput,
  useCreateAdminV2ContactNextActionMutation,
  useReconcileAdminV2ContactNextActionMutation,
} from "@/store/api/endpoints/adminV2ContactNextActionsApi";
import { getPublicErrorDetails } from "@/utils/errorHandling";
import { formatPublicErrorForDisplay } from "@/utils/errors/publicErrorContract";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const communicationAckMatches = (
  received: unknown,
  expected: ContactNextActionInput["communication"],
): boolean => {
  if (!expected) return received === null || received === undefined;
  return (
    isRecord(received) &&
    received.messageId === expected.messageId &&
    received.recipientId === expected.recipientId &&
    received.organisationId === expected.organisationId &&
    (expected.organisationId === undefined ||
      Object.prototype.hasOwnProperty.call(received, "organisationId"))
  );
};

export interface ContactNextActionSaveResult {
  item: ContactNextAction;
  wasIdempotentReplay: boolean;
  clientRequestId: string;
  personId: string;
  organisationId: string | null;
}
export interface ContactNextActionFormProps {
  /** Internal owner adapter; the same frozen body and exact ACK rules apply. */
  saveTransport?: (request: {
    personId: string;
    body: ContactNextActionInput;
  }) => Promise<ContactNextActionSaveResult>;
  requireScheduled?: boolean;
  onSaved?: () => void;
  personId: string;
  organisationId: string | null;
  communication?: ContactNextActionInput["communication"];
  fhcCapture?: { submissionId: string };
}

/** One owner retains this hook above keyed composition, scoped to the acting
 * staff member/contact/accepted receipt. The owner must discard it on actor
 * change. Server authority is freshly checked on every save/retry.
 * This is the Form's single save implementation, never a persisted permission. */
export const useContactNextActionFormSession = ({
  personId,
  organisationId,
  communication,
  fhcCapture,
  onSaved,
  saveTransport,
  requireScheduled = false,
}: ContactNextActionFormProps) => {
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState("");
  const [suggestion] = useState(() => suggestContactNextActionTime());
  const [dueAt, setDueAt] = useState(suggestion?.localInput ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const [create, state] = useCreateAdminV2ContactNextActionMutation();
  const [reconcile] = useReconcileAdminV2ContactNextActionMutation();
  const pending = useRef<ContactNextActionInput | null>(null);
  const pendingTransport =
    useRef<ContactNextActionFormProps["saveTransport"]>(undefined);
  const submitting = useRef(false);
  const pendingPersonId = useRef<string | null>(null);
  const outcomeUncertain = useRef(false);
  const readOwner = useRef({ personId, active: true });
  useEffect(() => {
    const owner = { personId, active: true };
    readOwner.current = owner;
    return () => {
      owner.active = false;
    };
  }, [personId]);
  /** A fresh read-context result permits presentation only. It cannot complete,
   * clear, replace or submit the frozen save; only its exact save ACK can. */
  const confirmOriginalContext = useCallback(
    async (currentOrganisationId: string | null): Promise<boolean> => {
      const body = pending.current;
      const requestPersonId = pendingPersonId.current;
      const owner = readOwner.current;
      const isCurrentOwner = () => owner.active && readOwner.current === owner;
      if (
        (currentOrganisationId !== null &&
          typeof currentOrganisationId !== "string") ||
        !body ||
        !requestPersonId ||
        requestPersonId !== personId ||
        owner.personId !== requestPersonId ||
        !owner.active
      )
        return false;
      try {
        const result: unknown = await reconcile({
          personId: requestPersonId,
          body: {
            clientRequestId: body.clientRequestId,
            organisationId: body.organisationId,
          },
        }).unwrap();
        return (
          isCurrentOwner() &&
          pending.current === body &&
          pendingPersonId.current === requestPersonId &&
          isRecord(result) &&
          result.clientRequestId === body.clientRequestId &&
          result.personId === requestPersonId &&
          result.organisationId === body.organisationId &&
          result.currentOrganisationId === currentOrganisationId &&
          (result.state === "unconfirmed" ||
            (result.state === "readable" && isRecord(result.item)))
        );
      } catch {
        return false;
      }
    },
    [personId, reconcile],
  );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    let body = pending.current;
    if (!body) {
      const due = dueAt ? new Date(dueAt) : null;
      if (requireScheduled && !due) {
        setError("Choose a due time for this follow-up.");
        return;
      }
      if (due && !Number.isFinite(due.getTime())) {
        setError("Choose a valid due time.");
        return;
      }
      body = {
        clientRequestId: crypto.randomUUID(),
        action,
        organisationId,
        dueAt: due?.toISOString() ?? null,
        unscheduledReason: due ? null : reason,
        ...(communication ? { communication: { ...communication } } : {}),
        ...(fhcCapture
          ? { fhcCapture: { submissionId: fhcCapture.submissionId } }
          : {}),
      };
      pending.current = body;
      pendingTransport.current = saveTransport;
      pendingPersonId.current = personId;
      outcomeUncertain.current = false;
    }
    const requestPersonId = pendingPersonId.current ?? personId;
    const owner = readOwner.current;
    const isCurrentOwner = () =>
      owner.active &&
      readOwner.current === owner &&
      owner.personId === requestPersonId;
    if (!isCurrentOwner()) return;
    submitting.current = true;
    setSaving(true);
    setError(null);
    try {
      const request = { personId: requestPersonId, body };
      const transport = pendingTransport.current;
      const result = transport
        ? await transport(request)
        : await create(request).unwrap();
      // A late ACK belongs to the original owner. It cannot clear a newer
      // Contact session, present protected work there, or call its callback.
      if (!isCurrentOwner()) return;
      if (
        result.clientRequestId !== body.clientRequestId ||
        result.personId !== requestPersonId ||
        result.organisationId !== body.organisationId ||
        !isRecord(result.item) ||
        !communicationAckMatches(
          result.item.communication,
          body.communication,
        ) ||
        (body.fhcCapture &&
          result.item.fhcCapture?.submissionId !== body.fhcCapture.submissionId)
      ) {
        outcomeUncertain.current = true;
        throw new Error("Next action confirmation did not match this request.");
      }
      setSavedPath(result.item.sourcePath);
      pending.current = null;
      pendingTransport.current = undefined;
      pendingPersonId.current = null;
      outcomeUncertain.current = false;
      setAction("");
      setDueAt("");
      setReason("");
      onSaved?.();
    } catch (cause) {
      if (!isCurrentOwner()) return;
      const details = getPublicErrorDetails(
        cause,
        "Next action could not be saved.",
      );
      const status =
        cause && typeof cause === "object" && "status" in cause
          ? cause.status
          : null;
      const definiteRejection =
        typeof status === "number" &&
        ([400, 403, 404, 422].includes(status) ||
          (status === 409 && details.errorCode !== "crm_next_action_busy"));
      // A later denial describes this attempt, not the original unknown
      // attempt. Keep its identity until a matching saved result is confirmed.
      const canCorrect = definiteRejection && !outcomeUncertain.current;
      if (!definiteRejection) outcomeUncertain.current = true;
      if (canCorrect) {
        pending.current = null;
        pendingTransport.current = undefined;
        pendingPersonId.current = null;
      }
      setError(
        formatPublicErrorForDisplay({
          ...details,
          message: canCorrect
            ? details.errorCode === "crm_person_scope_changed"
              ? "The contact changed. Your text is kept. Reload the contact context before saving a new action."
              : details.errorCode === "crm_person_in_cleanup_mode"
                ? "The contact is archived or combined. Your text is kept. Reopen it before saving a new action."
                : status === 409
                  ? "This request already has saved work or has changed. Your text is kept. Review saved next actions before starting a new save."
                  : "The next action was not saved. Your text is kept. Check access, the action and scheduling details, then try again."
            : "The save is not confirmed. Your text is kept. Check saved next actions, then retry this same save.",
          correlationId: details.correlationId ?? "unknown",
        }),
      );
    } finally {
      submitting.current = false;
      if (isCurrentOwner()) setSaving(false);
    }
  };
  const locked = saving || state.isLoading || pending.current !== null;
  return {
    personId: pendingPersonId.current ?? personId,
    action,
    setAction,
    suggestion,
    dueAt,
    setDueAt,
    chooseThirtyDayReminder: () => {
      if (submitting.current || pending.current !== null) return;
      const reminder = suggestThirtyDayContactReminder();
      if (!reminder) return;
      setDueAt(reminder.localInput);
      setReason("");
      setSavedPath(null);
      setError(null);
    },
    reason,
    setReason,
    error,
    savedPath,
    setSavedPath,
    submit,
    confirmOriginalContext,
    locked,
    isLoading: saving || state.isLoading,
    requireScheduled,
    hasPending: pending.current !== null,
    pendingRequestId: pending.current?.clientRequestId ?? null,
    pendingContextChanged:
      pending.current !== null &&
      (pending.current.organisationId !== organisationId ||
        pendingPersonId.current !== personId ||
        pending.current.fhcCapture?.submissionId !== fhcCapture?.submissionId),
  };
};
export type ContactNextActionFormSession = ReturnType<
  typeof useContactNextActionFormSession
>;
